/**
 * HoldStatusPanel.tsx — "Card and pre-authorisation": whether a card is on file
 * for this deal's order, and whether the month-1 hold is still good, asked LIVE
 * when the drawer opens.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 * CTO, 2026-09-11: "when you open the detail card, you make a call… figure out
 * whether it's valid or not". Lives in the deal drawer's OVERVIEW tab, directly
 * above the Patient journey panel; CLINICIAN and ADMIN both see it.
 *
 * ── WHY A LIVE READ AND NOT THE POSTURE ALREADY ON THE CARD ─────────────────
 * `deal.paymentPosture` comes off orders' LEDGER for a whole board page and
 * never asks Stripe, so a hold that died on Stripe's clock still reads
 * `authorised` there. `GET /v1/orders/:id/hold-status` asks Stripe about this
 * one order. The panel RENDERS that answer and decides nothing (R41): no money
 * arithmetic, no eligibility, no inference from an adjacent field.
 *
 * ── ⚠️ A FAILURE IS NEVER "NO" ───────────────────────────────────────────────
 * "Card on file: No" is a claim about a patient's money, and a false one does
 * real harm: a desk that believes it chases a patient who has already paid. So
 * a 404, a 5xx, a network fault, a body this build cannot read, and a deal we
 * could not ask about at all ALL render "Card status unavailable right now." —
 * never "No". "No" is printed only when orders-service said it, or when the
 * posture seam positively knows there is no order yet.
 *
 * ── WITH NO ORDER CASE THERE IS NOTHING TO ASK ───────────────────────────────
 * No id, no request. What is SAID then depends on why there is no id, because
 * the posture has three absences and none of them is "no card" (CLAUDE.md): a
 * posture of `no_order` — the ordinary state before checkout — is the only
 * reading that makes "no order yet" true. An absent posture (an older
 * clinic-service), a null one (a mutation echo) or `unknown` (orders-service
 * could not answer) all mean "we do not know", and the panel says that.
 *
 * ── ⚠️ MONEY ─────────────────────────────────────────────────────────────────
 * Clinicians never see money. orders-service sends a clinician's answer with no
 * `authorisedPence` key at all, and THIS component independently refuses to
 * print a figure for anyone but an ADMIN, even when handed one — the spec feeds
 * a clinician a leaky answer to prove this guard on its own. The amount prints
 * only beside the claim it annotates: HELD on a valid hold, TAKEN on a captured
 * one, and never under "expired" or "No", where it would read as money we have.
 *
 * ── ACCESSIBILITY ────────────────────────────────────────────────────────────
 * A region named by its visible heading. The answer sits in a polite
 * `role="status"` so it is announced when it lands — not `role="alert"`: the
 * drawer keeps its one assertive region for the action-needed banner. Every
 * state is words; nothing here is carried by colour.
 */
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { getOrderHoldStatus } from '../api/orders';
import type { DealCard, OrderHoldPreAuth, OrderHoldStatus, UserRole } from '../api/types';
import { formatLondonDateTime, formatLondonTime, formatPence } from '../lib/format';
import { Button } from '../ui/kit';
import { SectionTitleInline } from '../ui/sections';

/** The deal fields the panel reads — a narrow slice so the spec can build one. */
export type HoldStatusDeal = Pick<DealCard, 'orderCaseRef' | 'paymentPosture'>;

/**
 * The order case to ask about: the posture's live ref first — it is read from
 * the service that owns the order — then the card's own stamp, which the
 * order-sync writes and which is often null before checkout.
 */
export function holdStatusOrderCaseId(deal: HoldStatusDeal): string | null {
  return deal.paymentPosture?.orderCaseRef ?? deal.orderCaseRef ?? null;
}

const PRE_AUTH_STATES: readonly OrderHoldPreAuth[] = ['valid', 'expired', 'none', 'captured'];

/**
 * Only an answer this build can word is rendered. A body missing `cardOnFile`
 * would otherwise print "Card on file: No" off an `undefined` — the exact false
 * "no card" this panel exists to avoid — and a `preAuth` a newer orders-service
 * invents has no wording here yet. Both fall to "unavailable", the weaker claim.
 */
function isReadableHoldStatus(value: unknown): value is OrderHoldStatus {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.cardOnFile === 'boolean' &&
    PRE_AUTH_STATES.includes(candidate.preAuth as OrderHoldPreAuth)
  );
}

/** "Pre-authorised: …" — the four answers in words. */
function preAuthWords(status: OrderHoldStatus): string {
  switch (status.preAuth) {
    case 'valid': {
      const until = formatLondonDateTime(status.expiresAt);
      // A valid hold whose deadline cannot be read is still valid. Say that,
      // and do not invent a date for it.
      if (!until) return 'Yes — valid';
      return `Yes — valid until ${until}${status.expiresAtEstimated ? ' (estimated)' : ''}`;
    }
    case 'expired':
      return 'Yes — expired';
    case 'none':
      return 'No';
    case 'captured':
      return 'Payment taken';
  }
}

/**
 * The amount line, or null. `authorisedPence` covers two claims — held on
 * `valid`, taken on `captured` — so the verb comes from `preAuth`, and any other
 * answer prints nothing even if a figure arrived with it.
 *
 * ⚠️ THIS FUNCTION DOES NOT KNOW WHO IS LOOKING. The role gate is at the ONE call
 * site (see the panel below) rather than in here, so the refusal is impossible to
 * call without deciding it: a helper that took a role would let a future caller
 * pass the wrong one, and a helper that took none looks safe to reuse. Naming it
 * "the ADMIN amount line" is not a gate — that comment was here while a clinician
 * could read the figure.
 */
function amountLine(status: OrderHoldStatus): string | null {
  const pence = status.authorisedPence;
  if (typeof pence !== 'number' || !Number.isFinite(pence)) return null;
  if (status.preAuth === 'valid') return `${formatPence(pence)} held`;
  if (status.preAuth === 'captured') return `${formatPence(pence)} taken`;
  return null;
}

/** A finished read, tagged with the request it answers — see the effect. */
type Settled =
  | { orderCaseId: string; attempt: number; outcome: 'ready'; status: OrderHoldStatus }
  | { orderCaseId: string; attempt: number; outcome: 'unavailable' };

const LINE: CSSProperties = { margin: 0, fontSize: '13.5px', lineHeight: 1.45, color: 'var(--gps-text)' };
const NOTE: CSSProperties = { margin: 0, fontSize: '12px', lineHeight: 1.45, color: 'var(--gps-muted)' };

const UNAVAILABLE = 'Card status unavailable right now.';

export function HoldStatusPanel({ deal, role, style }: { deal: HoldStatusDeal; role: UserRole; style?: CSSProperties }) {
  const orderCaseId = holdStatusOrderCaseId(deal);
  // Bumped by "Check again" — a fresh request for the same order.
  const [attempt, setAttempt] = useState(0);
  const [settled, setSettled] = useState<Settled | null>(null);

  useEffect(() => {
    if (!orderCaseId) return undefined;
    // ⚠️ STALE ANSWERS. The drawer can be re-pointed at another deal, or
    // closed, while Stripe is still thinking. `live` drops an answer that
    // arrives after that; the id + attempt tag on `settled` (checked below)
    // stops the PREVIOUS order's answer painting for the one render before
    // this effect has run.
    let live = true;
    getOrderHoldStatus(orderCaseId).then(
      (status) => {
        if (!live) return;
        setSettled(
          isReadableHoldStatus(status)
            ? { orderCaseId, attempt, outcome: 'ready', status }
            : { orderCaseId, attempt, outcome: 'unavailable' },
        );
      },
      () => {
        if (live) setSettled({ orderCaseId, attempt, outcome: 'unavailable' });
      },
    );
    return () => {
      live = false;
    };
  }, [orderCaseId, attempt]);

  const answer =
    orderCaseId !== null && settled?.orderCaseId === orderCaseId && settled.attempt === attempt ? settled : null;

  let body: ReactNode;
  if (orderCaseId === null) {
    body = (
      <p style={LINE}>
        {deal.paymentPosture?.state === 'no_order' ? 'Card on file: No · Pre-authorised: No — no order yet' : UNAVAILABLE}
      </p>
    );
  } else if (answer === null) {
    body = <p style={NOTE}>Checking the card with Stripe…</p>;
  } else if (answer.outcome === 'unavailable') {
    body = <p style={LINE}>{UNAVAILABLE}</p>;
  } else {
    const { status } = answer;
    // ⚠️ THE PORTAL'S OWN MONEY GATE — ADMIN ONLY, and it is a real branch rather
    // than a comment. "Clinicians never see money" (Reshape §3.1) is enforced
    // server-side too: orders-service omits `authorisedPence` from a clinician's
    // answer entirely. This is the SECOND, INDEPENDENT refusal, and it is the one
    // that holds when a server ever answers a clinician with a figure — an older
    // revision, a widened @Roles, a projection somebody forgets. The spec feeds
    // this component exactly that leaky answer and asserts no figure renders.
    const amount = role === 'ADMIN' ? amountLine(status) : null;
    const checked = formatLondonTime(status.checkedAt);
    body = (
      <>
        <ul style={{ ...LINE, listStyle: 'none', padding: 0, display: 'grid', gap: '2px' }}>
          <li>{`Card on file: ${status.cardOnFile ? 'Yes' : 'No'}`}</li>
          <li>{`Pre-authorised: ${preAuthWords(status)}`}</li>
          {amount ? <li>{amount}</li> : null}
        </ul>
        {status.basis === 'estimate' ? (
          <p style={{ ...NOTE, marginTop: '4px' }}>
            We couldn&rsquo;t reach Stripe just now, so this is an estimate from when the card was authorised.
          </p>
        ) : null}
        {checked ? <p style={{ ...NOTE, marginTop: '4px' }}>{`Checked ${checked}`}</p> : null}
      </>
    );
  }

  return (
    <section
      aria-labelledby="deal-hold-status-title"
      style={{
        marginTop: 'var(--gps-s-4)',
        paddingTop: 'var(--gps-s-3)',
        borderTop: '1px solid var(--gps-border)',
        ...style,
      }}
    >
      <div style={{ marginBottom: 'var(--gps-s-2)' }}>
        <SectionTitleInline>
          <span id="deal-hold-status-title">Card and pre-authorisation</span>
        </SectionTitleInline>
      </div>
      <div role="status">{body}</div>
      {/* A retry only where a retry can help: a request that failed. With no
          order case there is nothing to re-ask. */}
      {answer?.outcome === 'unavailable' ? (
        <Button
          type="button"
          variant="ghost"
          onClick={() => setAttempt((n) => n + 1)}
          style={{ width: 'auto', marginTop: 'var(--gps-s-2)' }}
        >
          Check again
        </Button>
      ) : null}
    </section>
  );
}
